import { useLazyQuery } from '@apollo/client';
import equal from 'deep-equal';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import PrimaryButton from '@components/buttons/PrimaryButton';
import styles from '@components/forms/careProfile/CareProfile.module.css';
import CareProfileChange from '@components/forms/careProfile/CareProfileChange';
import CareProfileChangeFilters from '@components/forms/careProfile/CareProfileChangeFilters';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import { TimePeriodEnum } from '@typing/Enums';
import {
  GetCareProfileChangesDocument,
  GetCareProfileChangesQuery,
  GetCareProfileChangesQueryVariables,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { calculateDatesFromTimePeriod, sortByTimestamp } from '@utils/modelUtils';

type Filters = ComponentProps<typeof CareProfileChangeFilters>['filters'];

type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
};

const CareProfileHistory = ({ journey }: Props) => {
  const intl = useIntl();

  const [getCareProfileChanges, getCareProfileChangesQuery] = useLazyQuery<
    GetCareProfileChangesQuery,
    GetCareProfileChangesQueryVariables
  >(GetCareProfileChangesDocument);

  const [filters, setFilters] = useState<Filters>({
    timePeriod: TimePeriodEnum.ALL_TIME
  });

  const queryVariables = useMemo(() => {
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);
    return {
      endDate: dates.endDate && new Date(dates.endDate),
      journeyId: journey.id,
      startDate: dates.startDate && new Date(dates.startDate)
    };
  }, [filters.endDate, filters.startDate, filters.timePeriod, intl, journey.id]);

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }

    getCareProfileChanges({
      fetchPolicy: 'network-only',
      variables: queryVariables
    });
  }, [filters.endDate, filters.startDate, filters.timePeriod, getCareProfileChanges, queryVariables]);

  const careProfileChanges = useNodesFromConnection(getCareProfileChangesQuery.data?.careProfileChanges);

  const sortedCareProfileChanges = sortByTimestamp(
    sortByTimestamp(careProfileChanges, change => change.updatedAt),
    change => change.date
  );

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  return (
    <div>
      <div className={styles.careProfileChangeFilters}>
        <CareProfileChangeFilters filters={filters} onChange={handleFilterChange} />
      </div>
      {sortedCareProfileChanges.map(careProfileChange => (
        <CareProfileChange change={careProfileChange} key={careProfileChange.id} />
      ))}
      {getCareProfileChangesQuery.data?.careProfileChanges.pageInfo.hasNextPage && (
        <PrimaryButton
          data-test="purchase-button"
          onClick={() => {
            getCareProfileChangesQuery.fetchMore?.({
              variables: { cursor: getCareProfileChangesQuery.data?.careProfileChanges.pageInfo?.endCursor ?? '' }
            });
          }}
          textKey="dictionary.loadMore"
        />
      )}
    </div>
  );
};

export default CareProfileHistory;
